import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../redux/store';
import { useParams } from 'react-router-dom';
import { extendDataItem, mapTree } from '@progress/kendo-react-treelist';
import { useInternationalization } from '@progress/kendo-react-intl';
import useTranslate from '../../../../../common/hooks/useTranslate';

import { INote, IWidgetData } from '../../../../../common/services/http/analysisContentService';
import { ISetup } from '../../../types/ISetup';
import { ITreelistDefinition } from '../../../types/ITreelistDefinition';
import { ITreelistColumn } from '../../../types/ITreelistColumn';
import { ITreelistPreparedData } from '../../../types/ITreelistPreparedData';

import CashflowCell from '../cashflow/cells/cashflowCell/cashflowCell';
import TextCell from '../../../../ui/table/cells/textCell/textCell';
import GridHeaderCell from '../gridWidget/cell/gridHeaderCell/gridHeaderCell';

import { ICurrency } from '../../../types/ICurrency';
import { IWidget } from '../../../../../common/services/http/types/IWidget';
import { AnalysisContentWidgetModel } from '../../../../../models/AnalysisContentWidgetModel';
import { IMeasure } from '../../../types/IMeasure';
import { AnalysisContentWidgetDataModel } from '../../../../../models/AnalysisContentWidgetDataModel';

const subItemsField: string = 'childNodes';
const expandField: string = 'expanded';

const useGetTreelistData = (setup: ISetup, widget: AnalysisContentWidgetModel | IWidget, measures: IMeasure[], notes: INote[], boardId, scenarios, selectedObject, selectedPeriod, boardSelectedCurrency, investorObjectId) => {
  const [expanded, setExpanded] = useState<any[]>([]);
  const [selectedGrid, setSelectedGrid] = useState<number>(0);
  const [sectionsForGrid, setSectionsForGrid] = useState<string[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<ICurrency[]>([]);
  const [treelistContentEnriched, setTreelistContentEnriched] = useState<any[]>([]);
  const [formattedData, setFormattedData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currencies, setCurrencies] = useState<ICurrency[]>([]);
  const [treelistNotes, setTreelistNotes] = useState<INote[]>([]);
  const [totalWidth, setTotalWidth] = useState<string>('');

  const { t } = useTranslate();
  const intl = useInternationalization();
  const widgetDataRef = useRef(null);

  const investor = useSelector((state: RootState) => state.investor.investor);
  const { investorId } = useParams();

  const _measureHash = {};
  let _objectValueMap = {};
  let tempTreelistNotesArray: INote[] = [];

  const extractTreeListSectionsFromSetup = (): void => {
    let treeListSectionsArray: string[] = [];
    setup.definitions.map((definition: ITreelistDefinition) => {
      treeListSectionsArray.push(definition.name);
    });
    setSectionsForGrid(treeListSectionsArray);
  };

  const generateColumns = (measures: IMeasure[]): void => {
    _createMeasureHash(measures);
    generateColumnsForSection(_measureHash);
  };

  const _createMeasureHash = (measures: IMeasure[]): void => {
    if (measures) {
      for (let i = 0; i < measures.length; i++) {
        let measure = measures[i];
        _measureHash[measure.id] = measure;
      }
    }
  };

  const generateColumnsForSection = (measureHash: any): void => {
    if (notes) filterNotesForTreelist(notes);
    let copiedTreeListContent = setup.definitions[selectedGrid].columns.map((object) => ({ ...object }));

    let columns: any = [
      {
        field: 'name',
        title: t('WIDGET.GRID_TITLE'),
        expandable: true,
        width: '300px',
        locked: window.innerWidth > 960,
      },
    ];

    //@ts-ignore
    selectedCurrency && selectedCurrency.currency === '_' && columns.push({
      field: 'currency',
      title: t('WIDGET.GRID_CURRENCY'),
      width: '100px',
    });

    let treeWidth = 300;

    copiedTreeListContent.forEach((column) => {
      columns.push({
        field: `measure_${column.measureId}`,
        title: column.name,
        width: '225px',
        cell: column.viewCashflows ? createCFCellTreeList(column.name, measureHash) : createTextCellTreeList(),
        headerCell: createHeaderCellTreeList(column),
        notes: column.notes ? column.notes : null,
        hasNotes: !!column.hasNotes,
      });
      treeWidth += 225;
    });

    setTreelistContentEnriched(columns);
    setTotalWidth(`${treeWidth}px`);
  };

  const createCFCellTreeList = (columnName: string, hash: any) => {
    return (props) => <CashflowCell {...props}
                                    columnName={columnName}
                                    treelist
                                    hash={hash}
                                    objectValueMap={_objectValueMap}
                                    boardId={boardId}
                                    singlePeriod={setup.singlePeriod}
                                    selectedCurrency={selectedCurrency}
                                    investorObjectId={investorObjectId}/>;
  };

  const createTextCellTreeList = () => {
    return (props) => <TextCell {...props} treelist />;
  };

  const createHeaderCellTreeList = (column: ITreelistColumn) => {
    return (props) => <GridHeaderCell {...props} item={column} />;
  };

  const filterNotesForTreelist = (notes: INote[]) => {
    if (notes) {
      for (let i = 0; i < notes.length; i++) {
        for (let j = 0; j < notes[i].measures.length; j++) {
          let tempNoteMeasureId = notes[i].measures[j].measureId;
          setup.definitions[selectedGrid].columns.map((el) => {
            if (el.measureId === tempNoteMeasureId && !tempTreelistNotesArray.includes(notes[i])) {
              tempTreelistNotesArray.push(notes[i]);
            }
          });
        }
      }
    }

    setTreelistNotes(tempTreelistNotesArray);
  };

  const prepareData = (data: IWidgetData[], nodes): ITreelistPreparedData[] => {
    let preparedData: ITreelistPreparedData[] = [];
    _objectValueMap = {};

    for (let j = 0; j < nodes.length; j++) {
      _flattenNodesForDataTree(nodes[j], preparedData);
    }

    for (let i = 0; i < data.length; i++) {
      let row = data[i];
      let measure = _measureHash[row.measureId];
      if (measure) {
        let field = fieldNameFor('measure', measure.id);
        let objectValue = _objectValueMap[row['nodeId']];
        if (objectValue) {
          let tempMeasure = measures.find((item) => item.id === measure.id);
          let isPercent = tempMeasure.isPercent;
          let scale = tempMeasure.scale;
          let formattedAmount = intl.toString(row.amount, (isPercent ? 'p' : 'n') + scale);
          objectValue.row[field] = row.amount !== null ? formattedAmount : 'N/A';
          _createDataValueInfo(objectValue, row, objectValue.node, measure);
        }
      }
    }

    return preparedData;
  };

  const _flattenNodesForDataTree = (node, data, parentNode?): void => {
    let objectValue = {
      row: {
        objectId: node.objectId,
        name: node.name,
        nodeId: node.id,
        parentNodeId: parentNode ? parentNode.id : null,
        currency: node.currency,
      },
      map: {},
      node: node,
    };
    _objectValueMap[node.id] = objectValue;
    data.push(objectValue.row);

    for (let i = 0; i < node.childNodes.length; i++) _flattenNodesForDataTree(node.childNodes[i], data, node);
  };

  const _createDataValueInfo = (objectValue: any, dataRow: any, node: any, measure: IMeasure): void => {
    objectValue.map[measure.id] = {
      nodeId: node.id,
      scenarioId: node.scenarioId,
      valueId: dataRow.id,
    };
  };

  const fieldNameFor = (type: string, id: number): string => {
    return type + '_' + id;
  };

  const formatPreparedData = (preparedData: any[]): ITreelistPreparedData[] => {
    let hierarchyData: ITreelistPreparedData[] = [];
    for (let i = 0; i < preparedData.length; i++) {
      let tempElement = preparedData[i];
      let tempArray = [];
      for (let j = 0; j < preparedData.length; j++) {
        if (preparedData[j].parentNodeId === tempElement.nodeId) {
          tempArray.push(preparedData[j]);
        }
      }
      tempElement.childNodes = tempArray;
      if (tempElement.parentNodeId === null) {
        hierarchyData.push(tempElement);
      }
    }
    return hierarchyData;
  };

  const onExpandChange = (e): void => {
    setExpanded(e.value ? expanded.filter((id) => id !== e.dataItem.nodeId) : [...expanded, e.dataItem.nodeId]);
  };

  const addExpandField = (dataTree: any[]): any => {
    return mapTree(dataTree, subItemsField, (item) =>
      extendDataItem(item, subItemsField, {
        [expandField]: expanded.includes(item.nodeId),
      }),
    );
  };

  const processData = (): any => {
    return addExpandField(formattedData);
  };

  const selectTreeListSection = (id: number): void => {
    setSelectedGrid(id);
  };

  const handleCurrencyChange = (e) => {
    setSelectedCurrency(e.value);
  };

  const handleFindSelection = (currency, arrayToFind) => {
    if (arrayToFind.find((curr) => curr.currency === currency.currency)) {
      return currency;
    } else {
      return false;
    }
  };

  useEffect(() => {
    widgetDataRef.current = new AnalysisContentWidgetDataModel(widget, measures, investorId ? parseInt(investorId) : parseInt(investorObjectId), true, undefined);
    widgetDataRef.current.defineNodes(selectedObject);
  }, [investor]);

  useEffect(() => {
    if (boardSelectedCurrency) {
      let currency = handleFindSelection(boardSelectedCurrency, widgetDataRef.current.generateCurrencies(scenarios));
      if (currency) {
        setSelectedCurrency(currency);
      }
    }
  }, [boardSelectedCurrency]);

  useEffect(() => {
    //@ts-ignore
    if (selectedCurrency.currency) {
      extractTreeListSectionsFromSetup();
      setTreelistContentEnriched(setup.definitions[selectedGrid].columns);
      widgetDataRef.current.defineNodes(selectedObject);
      setCurrencies(widgetDataRef.current.generateCurrencies(scenarios));
      //@ts-ignore
      widgetDataRef.current.fetchData(selectedPeriod, selectedCurrency.currency).then((res: IWidgetData[]) => {
        let treeNodes = [];
        widgetDataRef.current.nodes.forEach((node) => {
          let tn = selectedObject.findNode(node.id);
          if (tn) treeNodes.push(tn);
        });
        widgetDataRef.current.groups.forEach((node) => {
          let tn = selectedObject.findNode(node.id);
          if (tn) treeNodes.concat(tn.childNodes);
        });

        let preparedData: ITreelistPreparedData[] = prepareData(res, treeNodes);
        let formattedData: ITreelistPreparedData[] = formatPreparedData(preparedData);
        setFormattedData(formattedData);
        setLoading(false);
      });

      generateColumns(measures);
    }
  }, [selectedObject, selectedPeriod, selectedCurrency, selectedGrid]);

  useEffect(() => {
    if (notes) filterNotesForTreelist(notes);
  }, [notes]);

  return {
    sectionsForGrid,
    treelistContentEnriched,
    onExpandChange,
    loading,
    treelistNotes,
    selectTreeListSection,
    processData,
    totalWidth,
    currencies,
    selectedCurrency,
    handleCurrencyChange,
  };
};

export default useGetTreelistData;
